import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

const formatMMSS = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Shared dialog that displays a super-admin-issued mobile login code with a live
 * countdown of its remaining validity (derived from the backend `expiresAt`).
 */
export default function MobileLoginCodeDialog({ codeData, onClose, onCopied }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!codeData?.expiresAt) {
      setRemaining(0);
      return undefined;
    }
    const expiry = new Date(codeData.expiresAt).getTime();
    const tick = () => setRemaining(Math.max(0, Math.round((expiry - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [codeData]);

  const expired = Boolean(codeData?.expiresAt) && remaining <= 0;

  return (
    <Dialog open={Boolean(codeData)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Mobile login code</DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#6B7280', mb: 2 }}>
          On the user&apos;s mobile app, enter phone{' '}
          <strong>{codeData?.countryCode || ''} {codeData?.phoneNumber || ''}</strong>, tap Send OTP, then
          enter this 4-digit code. It is single-use. Do not share it.
        </Typography>
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: 40,
            fontWeight: 800,
            letterSpacing: 10,
            color: expired ? '#9AA0A6' : '#1C1B1F',
            py: 1,
          }}
        >
          {codeData?.code}
        </Typography>
        <Typography sx={{ textAlign: 'center', fontSize: 14, fontWeight: 600, color: expired ? '#DA342E' : '#6B7280' }}>
          {expired ? 'Expired — generate a new code' : `Expires in ${formatMMSS(remaining)}`}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          disabled={expired}
          onClick={() => {
            if (codeData?.code && navigator?.clipboard) {
              navigator.clipboard.writeText(codeData.code);
              onCopied?.();
            }
          }}
          sx={{ textTransform: 'none' }}
        >
          Copy
        </Button>
        <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}
